import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AppHeader } from '@/components/app-header'
import { toast } from 'sonner'
import { FileText, Upload, Trash2, User, Calendar } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface KnowledgeBase {
  id: number
  portal_type: string
  file_name: string
  file_size: number
  uploaded_by: string
  created_at: string
  has_extracted_text: boolean
}

interface AiKnowledgeBasesProps {
  clinician_knowledge_bases: KnowledgeBase[]
  patient_knowledge_bases: KnowledgeBase[]
}

export default function AiKnowledgeBases({
  clinician_knowledge_bases,
  patient_knowledge_bases
}: AiKnowledgeBasesProps) {
  const [uploading, setUploading] = useState(false)
  const [deleteId, setDeleteId] = useState<number | null>(null)

  const handleUpload = async (portalType: 'clinician' | 'patient', file: File | null) => {
    if (!file) {
      toast.error('Please select a PDF file')
      return
    }

    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files are allowed')
      return
    }

    setUploading(true)
    const formData = new FormData()
    formData.append('pdf_file', file)
    formData.append('portal_type', portalType)

    try {
      const response = await fetch('/admin/ai_knowledge_bases', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: formData
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Knowledge base uploaded successfully')
        router.reload()
      } else {
        toast.error(data.errors?.[0] || 'Failed to upload file')
      }
    } catch (error) {
      toast.error('Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(`/admin/ai_knowledge_bases/${id}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })

      const data = await response.json()

      if (data.success) {
        toast.success('Knowledge base deleted')
        router.reload()
      } else {
        toast.error('Failed to delete knowledge base')
      }
    } catch (error) {
      toast.error('Failed to delete knowledge base')
    } finally {
      setDeleteId(null)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const renderKnowledgeBases = (kbs: KnowledgeBase[], portalType: 'clinician' | 'patient') => (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Upload PDF Document</CardTitle>
          <CardDescription>
            Upload a PDF file to add to the {portalType} knowledge base
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <Label htmlFor={`upload-${portalType}`} className="sr-only">
                Choose PDF file
              </Label>
              <Input
                id={`upload-${portalType}`}
                type="file"
                accept=".pdf"
                onChange={(e) => handleUpload(portalType, e.target.files?.[0] || null)}
                disabled={uploading}
              />
            </div>
          </div>
          <p className="text-muted-foreground mt-2 text-sm">
            The PDF text will be automatically extracted and used as context for AI responses.
          </p>
        </CardContent>
      </Card>

      {kbs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="text-muted-foreground mb-4 h-12 w-12" />
            <p className="text-muted-foreground text-center">
              No knowledge base documents uploaded yet.
              <br />
              Upload a PDF to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {kbs.map((kb) => (
            <Card key={kb.id}>
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <FileText className="text-muted-foreground h-8 w-8" />
                  <div>
                    <p className="font-medium">{kb.file_name}</p>
                    <div className="text-muted-foreground flex items-center gap-3 text-sm">
                      <span>{formatFileSize(kb.file_size)}</span>
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {kb.uploaded_by}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(kb.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleteId(kb.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <>
      <AppHeader
        breadcrumbs={[
          { label: 'Admin Panel', href: '/admin/console' },
          { label: 'AI Knowledge Bases' }
        ]}
        actions={
          <Button variant="outline" onClick={() => router.visit('/admin/ai_settings')}>
            AI Settings
          </Button>
        }
      />

      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">AI Knowledge Bases</h1>
          <p className="text-muted-foreground mt-1">
            Manage PDF documents that provide context to AI responses
          </p>
        </div>

        <Tabs defaultValue="clinician" className="w-full">
          <TabsList>
            <TabsTrigger value="clinician">
              Clinician Portal ({clinician_knowledge_bases.length})
            </TabsTrigger>
            <TabsTrigger value="patient">
              Patient Portal ({patient_knowledge_bases.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="clinician" className="mt-6">
            {renderKnowledgeBases(clinician_knowledge_bases, 'clinician')}
          </TabsContent>

          <TabsContent value="patient" className="mt-6">
            {renderKnowledgeBases(patient_knowledge_bases, 'patient')}
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Knowledge Base</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && handleDelete(deleteId)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
